// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
// Components, Layouts, Pages
import {
  BaseFilters,
  BaseInputSearch,
  BaseMoreAction,
  BaseSelect,
  BaseStatus,
  BaseTable,
  BaseToolbar,
  ConfirmModal,
  Loading,
  SortComponent,
} from '~/components/common';
import { PurchaseOrderChangeStatusFormModal, PurchaseOrderFormModal } from '~/components/module/purchaseOrder';
// Others
import useDebounce from '~/utils/hooks/useDebounce';
import { StorageEnum } from '~/utils/enum/base';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { formatDate, formatTitleCase, getFullName } from '~/utils/helper/common';
import { DateFormatEnum, PurchaseOrderEnum, RoleEnum, StatusEnum } from '~/utils/enum/common';
import { usePermissions } from '~/utils/hooks/userPermissions';
import { IPaginationResponse } from '~/utils/interface/base';
import { purchaseOrderActions } from '~/thunks/purchaseOrder/purchaseOrderSlice';
import { PURCHASE_ORDER_PERMISSIONS } from '~/utils/constants/permissions';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { IFormChangeStatusPO, IPurchaseOrder, IQueryParamPurchaseOrder } from '~/utils/interface/purchaseOrder';
import {
  IBaseMoreAction,
  IBaseOption,
  IBodyUpdate,
  IFilterValueChange,
  IHeaderCellType,
  IResultCallApiFn,
  ISortBy,
} from '~/utils/interface/common';
import {
  changeStatusPurchaseOrder,
  deletePurchaseOrder,
  getListPurchaseOrder,
} from '~/thunks/purchaseOrder/purchaseOrderThunk';
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SORT_PURCHASE_ORDER,
  EMPTY_STRING,
  EMPTY_VALUE,
  FILTER_DEFAULT_VALUES,
  optionTypePO,
} from '~/utils/constants/common';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
// Styles, images, icons
import styles from './PurchaseOrder.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columnsPurchaseOrder = (
  t: TFunction,
  sortBy: ISortBy[],
  handleSort: (sortType: string, name: string) => void,
  onView: (id: string) => void,
  onEdit: (id: string) => void,
  onChange: (item: IPurchaseOrder) => void,
  isDetail: boolean,
  isEdit: boolean
): IHeaderCellType<IPurchaseOrder>[] => {
  const getSortType = (name: string) => sortBy.find((item) => item.name === name)?.typeSort || EMPTY_STRING;

  return [
    {
      id: 'name',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_vendor_name')}</Box>
          <SortComponent name='name' sortType={getSortType('name')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IPurchaseOrder) => (
        <Stack direction={'row'} alignItems={'center'}>
          <Box className={cx('textName')}>{record?.vendorName}</Box>
        </Stack>
      ),
    },
    {
      id: 'type',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_type')}</Box>
          <SortComponent name='type' sortType={getSortType('type')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IPurchaseOrder) => (
        <Box className={cx('textValue')}>{formatTitleCase(record?.type) || EMPTY_VALUE}</Box>
      ),
    },
    {
      id: 'neededByDate',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_needed_by')}</Box>
          <SortComponent name='needed by' sortType={getSortType('needed by')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IPurchaseOrder) => (
        <Box className={cx('textValue')}>
          {formatDate(record?.neededByDate, DateFormatEnum.MM_DD_YYYY) || EMPTY_VALUE}
        </Box>
      ),
    },
    {
      id: 'approvedBy',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_approved_by')}</Box>
          <SortComponent name='approved by' sortType={getSortType('approved by')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IPurchaseOrder) => (
        <Box className={cx('textValue')}>
          {getFullName({ firstName: record?.approvedBy?.firstName, lastName: record?.approvedBy?.lastName }) ||
            EMPTY_VALUE}
        </Box>
      ),
    },
    {
      id: 'deliveryMethod',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_delivery_method')}</Box>
          <SortComponent name='method' sortType={getSortType('method')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IPurchaseOrder) => (
        <Box className={cx('textValue')}>{formatTitleCase(record?.method || EMPTY_STRING) || EMPTY_VALUE}</Box>
      ),
    },
    {
      id: 'status',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_status')}</Box>
          <SortComponent name='status' sortType={getSortType('status')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IPurchaseOrder) => <BaseStatus label={record?.status || EMPTY_STRING} />,
    },
    {
      id: 'action',
      label: t('common_text_action', { ns: 'common/base' }),
      minWidth: 100,
      align: 'center',
      render: (record: IPurchaseOrder) => {
        const actions: IBaseMoreAction[] = [
          ...(isDetail
            ? [
                {
                  label: t('table_action_view', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionView}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onView(record.id),
                },
              ]
            : []),
          ...(isEdit && record?.status !== StatusEnum.PO_RECEIVED
            ? [
                {
                  label: t('table_action_edit', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionEdit}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onEdit(record.id),
                },
              ]
            : []),
          ...(record?.status !== StatusEnum.PO_RECEIVED
            ? [
                {
                  label: t('common_action_change_status', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.commonIconRepeatBusiness}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onChange(record),
                },
              ]
            : []),
        ];

        return <BaseMoreAction actions={actions} />;
      },
    },
  ];
};

const PurchaseOrder = (props: Props) => {
  //#region Destructuring Props
  const {} = props;

  const { canCreatePurchaseOrders, canViewPurchaseOrders, canDetailPurchaseOrders, canEditPurchaseOrders } =
    usePermissions(PURCHASE_ORDER_PERMISSIONS);
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/purchaseOrder', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);

  const currentRole = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0];
  //#endregion Declare Hook

  //#region Selector
  const { isRefreshPurchaseOrderList } = useAppSelector((state) => state.purchaseOrder);
  //#endregion Selector

  //#region Declare State
  const [sortBy, setSortBy] = useState<ISortBy[]>(DEFAULT_SORT_PURCHASE_ORDER);
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [paramObject, setParamObject] = useState<IQueryParamPurchaseOrder>({
    page: pageSelected,
    limit: limitPage,
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();
  const [filterParams, setFilterParams] = useState<IQueryParamPurchaseOrder>(FILTER_DEFAULT_VALUES);

  const [purchaseOrderList, setPurchaseOrderList] = useState<IPurchaseOrder[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [idsPurchaseOrder, setIdsPurchaseOrder] = useState<string[]>([]);
  const [selectedItems, setSelectedItems] = useState<IPurchaseOrder[]>([]);
  const [idPurchaseOrder, setIdPurchaseOrder] = useState<string>(EMPTY_STRING);
  const [currentStatus, setCurrentStatus] = useState<string>(EMPTY_STRING);
  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);
  const [isShowChangeStatusModal, setIsShowChangeStatusModal] = useState<boolean>(false);
  const [isOpenPurchaseOrderFormModal, setIsOpenPurchaseOrderFormModal] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListPurchaseOrder(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshPurchaseOrderList) return;

    handleGetListPurchaseOrder(paramObject);

    dispatch(purchaseOrderActions.setRefreshPurchaseOrderList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshPurchaseOrderList]);

  useEffect(() => {
    if (pagination || debounceSearchKey) {
      setParamObject({
        ...paramObject,
        page: DEFAULT_CURRENT_PAGE,
        limit: limitPage,
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
      });

      setSearchParams({
        page: String(DEFAULT_CURRENT_PAGE),
        limit: String(limitPage),
        ...(searchKey ? { textSearch: debounceSearchKey } : {}),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListPurchaseOrder = (params: IQueryParamPurchaseOrder) => {
    setIsLoading(true);

    dispatch(getListPurchaseOrder(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination, totalItems } = res?.data;

        if (!responses) return;

        setPurchaseOrderList(responses);
        setPagination(pagination);

        setTotalItems(totalItems);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEdit = (id: string) => {
    if (!id) return;

    setIdPurchaseOrder(id);
    setIsOpenPurchaseOrderFormModal(true);
  };

  const handleChangeStatusTable = (item: IPurchaseOrder) => {
    if (!item?.id) return;

    setCurrentStatus(item?.status);
    setIdPurchaseOrder(item?.id);
    setIsShowChangeStatusModal(true);
  };

  const handleCloseConfirmDelete = () => {
    setIdPurchaseOrder(EMPTY_STRING);
    setIsShowConfirmDelete(false);
  };

  const handleView = (id: string) => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.detailPurchaseOrder(id));
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.detailPurchaseOrder(id));
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.detailPurchaseOrder(id));
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.detailPurchaseOrder(id));
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.detailPurchaseOrder(id));
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.detailPurchaseOrder(id));
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.detailPurchaseOrder(id));
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.detailPurchaseOrder(id));
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.detailPurchaseOrder(id));
        break;
    }
  };

  const handleConfirmDelete = () => {
    setIsLoading(true);

    dispatch(deletePurchaseOrder(idPurchaseOrder))
      .unwrap()
      .then(() => {
        setIdPurchaseOrder(EMPTY_STRING);
        handleCloseConfirmDelete();
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleRowsSelect = (ids: string[], selectedItems?: IPurchaseOrder[]) => {
    setIdsPurchaseOrder(ids);
    setSelectedItems(selectedItems || []);
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleSort = (sortType: string, name: string) => {
    const updatedSortBy = sortBy.map((item) => ({
      ...item,
      typeSort: item.name === name ? sortType : undefined,
    }));
    setSortBy(updatedSortBy);
    setParamObject({ ...paramObject, sortBy: name, sortType: sortType });
    setSearchParams({ ...params, sortBy: name, sortType: sortType });
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleShowPurchaseOrderFormModal = () => {
    setIsOpenPurchaseOrderFormModal((prev) => !prev);
    setIdPurchaseOrder(EMPTY_STRING);
  };

  const handleChangeStatus = (status: string) => {
    const body: IBodyUpdate<IFormChangeStatusPO> = {
      id: idPurchaseOrder,
      body: {
        status: status,
      },
    };

    dispatch(purchaseOrderActions.setRefreshPurchaseOrderList(true));

    dispatch(changeStatusPurchaseOrder(body))
      .unwrap()
      .then((res) => {})
      .catch((_error) => {})
      .finally(() => {
        dispatch(purchaseOrderActions.setRefreshPurchaseOrderList(false));
      });
  };

  const fetchOptionFilter = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionTypePO,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const handleFilterTypeChange = (
    typeOption: IBaseOption,
    onChange?: (value: IFilterValueChange<IQueryParamPurchaseOrder>) => void
  ) => {
    if (!typeOption) return;

    const { value } = typeOption;

    onChange?.({ name: 'type', value: value });
    setFilterParams({ ...filterParams, type: value });
  };

  const handleApplyFilter = (values: IQueryParamPurchaseOrder | undefined) => {
    if (!values) {
      setFilterParams({});
      return;
    }

    setFilterParams({
      ...values,
    });

    if (values) {
      setParamObject({
        ...paramObject,
        type: values?.type,
      });
    }
  };

  const handleClearFilter = () => {
    setFilterParams(FILTER_DEFAULT_VALUES);
    setParamObject({
      ...paramObject,
      page: DEFAULT_CURRENT_PAGE,
      limit: limitPage,
      type: undefined,
    });
  };

  const handleClose = () => {
    setIsShowChangeStatusModal((prev) => !prev);
    setIdPurchaseOrder(EMPTY_STRING);
    setCurrentStatus(EMPTY_STRING);
  };
  //#endregion Handle Function

  return (
    <Box id='purchaseOrderPage' className={cx('container')}>
      <BaseToolbar title={t('purchase_order_title_management')}>
        <BaseInputSearch
          width={200}
          typeStyle='outlined'
          onChange={handleSearchChange}
          borderRadius={100}
          placeholder={t('common_text_search', { ns: 'common/base' })}
        />

        <BaseFilters<IQueryParamPurchaseOrder>
          value={filterParams}
          defaultValue={FILTER_DEFAULT_VALUES}
          onApply={handleApplyFilter}
          onClear={handleClearFilter}
        >
          {({ value, onChange }) => (
            <Stack direction={'column'} width={340} mb={4} gap={6}>
              <BaseSelect
                gap={8}
                styleType='contained'
                label={t('purchase_order_title_type')}
                placeholder={t('purchase_order_title_select_type')}
                callApiFn={fetchOptionFilter}
                value={value?.type}
                onChange={(option) => handleFilterTypeChange(option as any, onChange)}
              />
            </Stack>
          )}
        </BaseFilters>

        {canCreatePurchaseOrders && (
          <Box className={cx('button')}>
            <Button
              type='button'
              variant='contained'
              className={cx('buttonAction')}
              onClick={handleShowPurchaseOrderFormModal}
              startIcon={<img src={icons.commonIconPlus} alt={'common_alt_img'} />}
            >
              {t('purchase_order_text_add_purchase_order')}
            </Button>
          </Box>
        )}
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          dataSource={canViewPurchaseOrders ? purchaseOrderList : []}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columnsPurchaseOrder(
            t,
            sortBy,
            handleSort,
            handleView,
            handleEdit,
            handleChangeStatusTable,
            canDetailPurchaseOrders,
            canEditPurchaseOrders
          )}
          onPageChange={handlePageChange}
          // dataSelectedIds={idsPurchaseOrder}
          // onSelectChange={handleRowsSelect}
          {...(canDetailPurchaseOrders ? { onRowClick: handleView } : {})}
        />
      </Box>

      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isShowChangeStatusModal && (
        <PurchaseOrderChangeStatusFormModal status={currentStatus} onApply={handleChangeStatus} onClose={handleClose} />
      )}

      {isLoading && <Loading />}

      {isOpenPurchaseOrderFormModal && (
        <PurchaseOrderFormModal
          onClose={handleShowPurchaseOrderFormModal}
          idPurchaseOrder={idPurchaseOrder}
          type={PurchaseOrderEnum.PURCHASE}
        />
      )}
    </Box>
  );
};

export default PurchaseOrder;
